import { cn } from '@invent/wl-ui-kit-next';
import { Alert, AlertDescription, AlertTitle, Input, Switch } from '@invent/wl-ui-kit-next/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { HelpPopover, PageHeader } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IListField, IListFieldHiddenRule, IListFieldUsageView } from '../../api/lists-contracts';
import { NAME_FIELD } from '../../list-criteria';
import { listsApi } from '../../api/lists-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { listSurveyLabel, originLabel } from '../../lists';
import { TEAM_PATHS, reportPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { SurveyTabs } from '../lists/area';
import { matchesField, useFieldFolds } from '../lists/field-picker';

/**
 * Reports → Fields (SR-462) — the fields an administrator hides from the team's field pickers, so
 * that of several fields saying one thing (a firm's name is a profile column, a survey answer and
 * an ADV field) the team builds reports on the same one. `internal_fields.author`, the policy
 * survey-api hides under.
 *
 * A survey's catalogue (`?survey=`, the Reports tabs) in its origins, each with a switch for the
 * whole origin and one per field. **A rule holds in every survey**: survey-api keys it by the field's
 * code or the origin, and marks the field `hidden` in each catalogue that holds it. Hidden is about
 * choosing alone — a saved report on a hidden field still reads and shows it.
 */
export function AdminReportFieldsPage() {
  const [params] = useSearchParams();
  const survey = params.get('survey') || 'FA';
  const [search, setSearch] = React.useState('');
  const { data, error, reload } = useAdminData(
    async (signal) => {
      const [catalogue, rules, usage] = await Promise.all([
        listsApi.fields(survey, signal),
        listsApi.hiddenFields(signal),
        listsApi.fieldUsage(signal)
      ]);
      return { survey, catalogue, rules, usage };
    },
    [survey]
  );
  const answer = data?.survey === survey ? data : null;
  const [busy, setBusy] = React.useState<Nullable<string>>(null);
  const [failed, setFailed] = React.useState<Nullable<string>>(null);
  // The origins start folded, as the field picker's groups do; a search opens those it finds in.
  const folds = useFieldFolds(search);

  const rules = React.useMemo(
    () => new Map((answer?.rules ?? []).map((rule) => [`${rule.kind}:${rule.key}`, rule])),
    [answer]
  );
  const usage = React.useMemo(() => {
    const views = new Map((answer?.usage.views ?? []).map((view) => [view.id, view]));
    return new Map(
      (answer?.usage.fields ?? []).map((field) => [
        field.code,
        { views: field.views.flatMap((id) => views.get(id) ?? []), privateOfOthers: field.privateOfOthers }
      ])
    );
  }, [answer]);
  const fields = React.useMemo(() => answer?.catalogue?.groups.flatMap((group) => group.fields) ?? [], [answer]);
  const hiddenCount = fields.filter((field) => field.hidden).length;
  const origins = byOrigin(fields)
    .map((origin) => ({ ...origin, shown: origin.fields.filter((field) => matchesField(field, search, true)) }))
    .filter((origin) => origin.shown.length > 0);

  const toggle = async (kind: IListFieldHiddenRule['kind'], key: string, hidden: boolean) => {
    setBusy(`${kind}:${key}`);
    setFailed(null);
    try {
      await listsApi.setHidden(kind, key, hidden);
      await reload();
    } catch (problem) {
      setFailed(refusalMessage(problem, 'The change was not saved.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Report fields"
        crumb="Reports"
        tabs={<SurveyTabs current={survey} replace />}
        actions={<AboutFields />}
      />
      <div className="grid min-w-0 gap-4 px-6 py-6">
        {failed && (
          <Alert variant="destructive">
            <AlertDescription>{failed}</AlertDescription>
          </Alert>
        )}
        {error && answer !== null && (
          <Alert variant="destructive">
            <AlertTitle>Could not refresh — showing the fields as last read</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-wrap items-center gap-4">
          <Input
            type="search"
            aria-label="Search the fields"
            placeholder="Search fields…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-9 w-full sm:max-w-96"
          />
          {answer?.catalogue && (
            <span className="text-sm text-muted-foreground" role="status">
              {hiddenCount === 0 ? 'Every field is offered' : `${hiddenCount} hidden`}
            </span>
          )}
        </div>
        {answer === null ? (
          error ? (
            <LoadError message={`The fields could not be read. ${error}`} />
          ) : (
            <p role="status" className="py-8 text-center text-muted-foreground">
              Reading the fields…
            </p>
          )
        ) : answer.catalogue === null ? (
          <p role="status" className="py-8 text-center text-muted-foreground">
            This survey is not on this deployment.
          </p>
        ) : origins.length === 0 ? (
          <p role="status" className="py-8 text-center text-muted-foreground">
            {search.trim() ? `No field matches “${search.trim()}”.` : 'This survey has no fields yet.'}
          </p>
        ) : (
          origins.map((origin) => {
            const rule = rules.get(`origin:${origin.origin}`);
            const unfolded = folds.isOpen(origin.origin);
            const hidden = origin.fields.filter((field) => field.hidden).length;
            return (
              <section
                key={origin.origin}
                aria-label={originLabel(origin.origin)}
                data-origin={origin.origin}
                className="rounded-lg border border-border bg-white"
              >
                <header
                  className={cn(
                    'flex flex-wrap items-center justify-between gap-3 px-4 py-2.5',
                    unfolded && 'border-b border-border'
                  )}
                >
                  <h2 className="text-sm font-semibold">
                    <button
                      type="button"
                      aria-expanded={unfolded}
                      onClick={() => folds.toggle(origin.origin)}
                      className="flex items-center gap-1.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {unfolded ? (
                        <ChevronDown className="size-4 text-muted-foreground" aria-hidden />
                      ) : (
                        <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                      )}
                      {originLabel(origin.origin)}
                      <span className="font-normal text-muted-foreground">
                        · {origin.fields.length} {origin.fields.length === 1 ? 'field' : 'fields'}
                        {hidden > 0 && rule === undefined && ` · ${hidden} hidden`}
                      </span>
                    </button>
                  </h2>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{rule ? hiddenWords(rule) : 'Offered'}</span>
                    <Switch
                      aria-label={`Hide every ${originLabel(origin.origin)} field`}
                      checked={rule !== undefined}
                      disabled={busy !== null}
                      onCheckedChange={(on) => void toggle('origin', origin.origin, on)}
                    />
                    <span aria-hidden>Hide all</span>
                  </div>
                </header>
                {unfolded && (
                  <ul className="divide-y divide-border">
                    {origin.shown.map((field) => {
                      const own = rules.get(`field:${field.code}`);
                      const byOriginRule = rule !== undefined && own === undefined;
                      return (
                        <li key={field.code} data-field={field.code} className="grid gap-1 py-1.5 pr-4 pl-10">
                          <div className="flex items-center justify-between gap-4">
                            <span className={field.hidden ? 'grid text-muted-foreground' : 'grid'}>
                              <span className="text-sm">
                                {field.number && <span className="mr-1 tabular-nums">{field.number}</span>}
                                {field.label}
                              </span>
                              <span className="font-mono text-xs text-muted-foreground">{field.code}</span>
                            </span>
                            <div className="flex shrink-0 items-center gap-2 text-sm">
                              <span className="text-muted-foreground">
                                {field.code === NAME_FIELD
                                  ? 'Always offered'
                                  : own
                                    ? hiddenWords(own)
                                    : byOriginRule
                                      ? 'Hidden with its origin'
                                      : ''}
                              </span>
                              <Switch
                                aria-label={`Hide ${field.label}`}
                                checked={field.hidden === true}
                                disabled={busy !== null || byOriginRule || field.code === NAME_FIELD}
                                onCheckedChange={(on) => void toggle('field', field.code, on)}
                              />
                            </div>
                          </div>
                          {field.hidden && <StillUsedIn usage={usage.get(field.code)} />}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })
        )}
      </div>
    </>
  );
}

/** Where a hidden field still stands: the reports and lists that use it, each a link to it, and
 *  how many private ones of other people, which are not named. Nothing where none does. */
function StillUsedIn({ usage }: { usage?: { views: IListFieldUsageView[]; privateOfOthers: number } }) {
  if (!usage || (usage.views.length === 0 && usage.privateOfOthers === 0)) return null;
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs" data-used-in>
      <span className="text-muted-foreground">Still used in</span>
      {usage.views.map((view) => (
        <Link
          key={view.id}
          to={
            view.kind === 'report'
              ? reportPath(view.id)
              : `${TEAM_PATHS.lists}?${new URLSearchParams({ survey: view.survey, view: view.id })}`
          }
          className="inline-flex items-baseline gap-1 underline underline-offset-2 hover:text-foreground"
        >
          <span className="text-muted-foreground no-underline">
            {view.kind === 'report' ? 'Report' : 'List'} · {listSurveyLabel(view.survey)}:
          </span>
          {view.name}
        </Link>
      ))}
      {usage.privateOfOthers > 0 && (
        <span className="text-muted-foreground">
          and {usage.privateOfOthers} private {usage.privateOfOthers === 1 ? 'view' : 'views'} of others
        </span>
      )}
    </div>
  );
}

/** "Hidden by ann@… · 29 Oct 2026 14:02" — who hid it, when. */
function hiddenWords(rule: IListFieldHiddenRule): string {
  return `Hidden${rule.hiddenBy ? ` by ${rule.hiddenBy.email}` : ''} · ${formatDateTime(rule.hiddenAt)}`;
}

/** The catalogue's fields by their provenance's origin, in the order the catalogue first reaches
 *  each; a field with no provenance under Record. */
function byOrigin(fields: readonly IListField[]): { origin: string; fields: IListField[] }[] {
  const groups = new Map<string, IListField[]>();
  for (const field of fields) {
    const origin = field.provenance?.origin ?? 'record';
    const members = groups.get(origin) ?? [];
    members.push(field);
    groups.set(origin, members);
  }
  return [...groups].map(([origin, members]) => ({ origin, fields: members }));
}

/** The "?": what hiding does, and where. */
function AboutFields() {
  return (
    <HelpPopover label="About report fields" align="end">
      <p>
        A hidden field is not offered to anyone when they choose a column, a filter, a sort or an export
        layout field — in Reports, Lists and the work pages. Hide the extra fields that say the same
        thing, so the team builds reports on the same one.
      </p>
      <p>
        A field is hidden in every survey that has it. <strong>Hide all</strong> hides every field of
        that origin, including ones added later.
      </p>
      <p>
        Reports and lists that already use a hidden field keep it and still show its values; the field
        is marked <strong>Hidden by admin</strong> there, for the owner to replace. Under a hidden field
        are the reports and lists that still use it.
      </p>
    </HelpPopover>
  );
}
